import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  YANDEX_CONSENTS_KEY,
  YANDEX_POPUP_FLAG,
  YANDEX_POPUP_TTL,
  YANDEX_RESULT_KEY,
  type YandexAuthResult
} from "@features/auth/hooks/useYandexAuth";
import { type ConsentData, useAuth } from "@shared/contexts/auth-context";
import { useToast } from "@shared/helper_lib/hooks/use-toast";

export const YandexCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { signInWithYandex } = useAuth();
  const { toast } = useToast();
  const isProcessed = useRef(false);
  const [isPopupDone, setIsPopupDone] = useState(false);

  useEffect(() => {
    if (isProcessed.current) {
      return;
    }
    isProcessed.current = true;

    // Открыто как окно авторизации: результат отдаём вкладке сайта через localStorage
    const popupStartedAt = Number(localStorage.getItem(YANDEX_POPUP_FLAG));
    const isPopup = Boolean(popupStartedAt) && Date.now() - popupStartedAt < YANDEX_POPUP_TTL;
    if (isPopup) {
      localStorage.removeItem(YANDEX_POPUP_FLAG);
    }

    const finishPopup = (result: YandexAuthResult): void => {
      localStorage.setItem(YANDEX_RESULT_KEY, JSON.stringify(result));
      setIsPopupDone(true);
      window.close();
    };

    const handleCallback = async (): Promise<void> => {
      // Яндекс возвращает токен в hash фрагменте URL (#access_token=...)
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const accessToken = params.get("access_token");

      // Восстанавливаем consents из localStorage
      let consents: ConsentData | undefined;
      const storedConsents = localStorage.getItem(YANDEX_CONSENTS_KEY);
      if (storedConsents) {
        try {
          consents = JSON.parse(storedConsents);
          localStorage.removeItem(YANDEX_CONSENTS_KEY);
        } catch (error) {
          console.error("Failed to parse stored consents:", error);
        }
      }

      if (accessToken) {
        try {
          await signInWithYandex(accessToken, consents);

          if (isPopup) {
            finishPopup({ success: true });
            return;
          }

          navigate("/");
        } catch (error) {
          console.error("Yandex callback error:", error);

          let errorMessage = "Ошибка при авторизации через Яндекс";
          let isNewUserError = false;

          if (typeof error === "object" && error !== null && "response" in error) {
            const response = (error as { response?: { data?: { message?: string } } }).response;
            const messageText = response?.data?.message;

            if (messageText === "Необходимо принять условия оферты и политику конфиденциальности") {
              isNewUserError = true;
              errorMessage = "Пожалуйста, зарегистрируйтесь, приняв условия оферты и политику конфиденциальности";
            } else {
              errorMessage = messageText || errorMessage;
            }
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }

          if (isPopup) {
            finishPopup({ success: false, message: errorMessage, isNewUser: isNewUserError });
            return;
          }

          navigate("/", {
            state: {
              authError: {
                message: errorMessage,
                isNewUser: isNewUserError
              }
            }
          });
        }
      } else {
        const errorMessage = "Не удалось получить токен доступа от Яндекса";

        if (isPopup) {
          finishPopup({ success: false, message: errorMessage, isNewUser: false });
          return;
        }

        navigate("/", {
          state: {
            authError: {
              message: errorMessage,
              isNewUser: false
            }
          }
        });
      }
    };

    handleCallback();
  }, [navigate, signInWithYandex, toast]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold mb-2">
          {isPopupDone ? "Авторизация завершена" : "Авторизация через Яндекс..."}
        </h2>
        <p className="text-muted-foreground">
          {isPopupDone ? "Можно закрыть это окно и вернуться на сайт" : "Пожалуйста, подождите"}
        </p>
      </div>
    </div>
  );
};
